import { useState, useEffect, useRef } from 'react';
import { historicoService, type HistoricoAPI } from '../services/historico';
import type { HistoryItem } from '../components/Timeline/HistoryTimeline';

/**
 * Formatar data sem problemas de timezone
 * Para strings no formato YYYY-MM-DD vindas do backend
 */
const formatDateSafe = (value: any): string => {
  if (!value) return 'vazio';
  
  // Se é uma string no formato YYYY-MM-DD (padrão do backend)
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Fallback para outros formatos (evita timezone issues)
  try {
    return new Date(value + 'T12:00:00').toLocaleDateString('pt-BR');
  } catch {
    return value.toString();
  }
};

/**
 * Transforma dados da API em formato HistoryItem
 */
const transformHistoricoToHistoryItem = (historico: HistoricoAPI): HistoryItem => {
  // Mapear ações para tipos
  const getActionType = (acao: string, h: HistoricoAPI): HistoryItem['type'] => {
    const acaoLower = acao.toLowerCase();

    // Priorizar detecção de eventos relacionados a anexo
    if (acaoLower.includes('anexo')) {
      // Palavras que indicam remoção/desativação/exclusão
      // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
      const removalHints = ['desativ', 'remov', 'exclu', 'apag'];
      if (removalHints.some(hint => acaoLower.includes(hint)) || (h.anexo && Boolean(h.anexo.active))) {
        return 'attachment_removed';
      }
      return 'attachment_added';
    }

    // Detecção de observadores
    if (acaoLower.includes('observ')) {
      return 'watched';
    }

    // Detecção de autorizações/reprovações
    if (acaoLower.includes('autorizou') || acaoLower.includes('aprovou')) {
      return 'status_changed';
    }
    if (acaoLower.includes('reprovou')) {
      return 'status_changed';
    }

    // Detecção de criação
    if (acaoLower.includes('criou') || acaoLower.includes('criado')) {
      return 'created';
    }
    
    // Detecção de atribuições
    if (acaoLower.includes('atribuiu') || acaoLower.includes('puxar') || acaoLower.includes('puxou')) {
      return 'assigned';
    }
    
    // Detecção de remoção de atribuído
    if (acaoLower.includes('removeu um atribuido') || acaoLower.includes('removeu um atribuído')) {
      return 'assigned';
    }
    
    // Detecção de mudanças de status (solucionou, finalizou, devolveu, etc)
    if (
        acaoLower.includes('autorizou') ||
        acaoLower.includes('reprovou') ||
        acaoLower.includes('aprovou') ||
        acaoLower.includes('recusou') ||
        acaoLower.includes('aceitou') ||
        acaoLower.includes('solucion') ||
        acaoLower.includes('desfez') ||
        acaoLower.includes('finaliz') ||
        acaoLower.includes('devol') ||
        acaoLower.includes('puxou') ||
        acaoLower.includes('puxar') ||
        acaoLower.includes('atribuiu') ||
        acaoLower.includes('errada') ||
        acaoLower.includes('remand') ||
        acaoLower.includes('reenviou') ||
        acaoLower.includes('excluiu') ||
        acaoLower.includes('removeu um atribuido') ||
        acaoLower.includes('removeu um atribuído') ||
        (acaoLower.includes('desatrib') && acaoLower.includes('demanda'))
    ) {
      return 'status_changed';
    }
    
    // Detecção de mudança de status genérica
    if (acaoLower.includes('status')) {
      return 'status_changed';
    }
    
    // Detecção de mudança de prioridade
    if (acaoLower.includes('prioridade')) {
      return 'priority_changed';
    }
    
    // Detecção de comentários
    if (acaoLower.includes('comentou') || acaoLower.includes('comentário')) {
      return 'commented';
    }
    
    // Detecção de edições
    if (acaoLower.includes('editou') || acaoLower.includes('atualizou')) {
      return 'updated';
    }

    return 'updated'; // Fallback
  };

  // Gerar descrição amigável
  const getDescription = (historico: HistoricoAPI): string => {
    // Para ações com comentários, não incluir o comentário na descrição principal
    // pois ele será exibido separadamente no details
    return historico.acao;
  };

  // Gerar detalhes adicionais
  const getDetails = (historico: HistoricoAPI) => {
    const details: any = {};
    
    // Sempre processar comentários se existirem
    if (historico.comentario && historico.comentario.trim()) {
      details.comment = historico.comentario.trim();
    }

    // Processar usuário afetado (ex: observador adicionado/removido, atribuído removido)
    if (historico.afetado) {
      details.affected_name = historico.afetado.name;
      details.affected_email = historico.afetado.email;
    }
    
    // Processar anexos
    if (historico.anexo) {
      details.attachment_name = historico.anexo.nome_anexo;
    }
    
    // Processar mudanças de edição
    if (historico.edicao && historico.edicao.changes) {
      const changes = historico.edicao.changes;
      
      // Para mudanças de status - extrair o valor antigo
      if (changes.status) {
        details.from = changes.status;
        // O valor novo virá do snapshot da edição
        if (historico.edicao.snapshot && historico.edicao.snapshot.status) {
          details.to = historico.edicao.snapshot.status;
        }
      }
      
      // Para mudanças de prioridade
      if (changes.prioridade) {
        details.from = changes.prioridade;
        if (historico.edicao.snapshot && historico.edicao.snapshot.prioridade) {
          details.to = historico.edicao.snapshot.prioridade;
        }
      }
      
      // Para mudanças de data
      if (changes.sugestao_entrega) {
        details.from = formatDateSafe(changes.sugestao_entrega);
        if (historico.edicao.snapshot && historico.edicao.snapshot.sugestao_entrega) {
          details.to = formatDateSafe(historico.edicao.snapshot.sugestao_entrega);
        }
      }
      if (changes.previsao_entrega) {
        details.from = formatDateSafe(changes.previsao_entrega);
        if (historico.edicao.snapshot && historico.edicao.snapshot.previsao_entrega) {
          details.to = formatDateSafe(historico.edicao.snapshot.previsao_entrega);
        }
      }
    }
    
    // Retornar details apenas se houver conteúdo
    return Object.keys(details).length > 0 ? details : undefined;
  };

  return {
    id: historico.id.toString(),
  type: getActionType(historico.acao, historico),
    user: {
      id: historico.editor.id,
      name: historico.editor.name,
      email: historico.editor.email,
      funcao_usuario: historico.editor.funcao_usuario,
      cargo_usuario: historico.editor.cargo_usuario,
      // avatar opcional - não incluir se for undefined
    },
    timestamp: historico.created_at,
    description: getDescription(historico),
    details: getDetails(historico),
  };
};

/**
 * Transforma um item de histórico com edições em um HistoryItem expansível
 * @param customDescription - Descrição customizada opcional para a ação (ex: "reenviou a demanda")
 */
const transformHistoricoToHistoryItemWithChanges = (
  historico: HistoricoAPI, 
  estadoPosterior: any,
  customDescription?: string
): HistoryItem => {
  // Mapear nomes de campos para labels amigáveis
  const fieldLabels: Record<string, string> = {
    'descricao': 'Descrição',
    'sugestao_entrega': 'Data sugerida para entrega',
    'previsao_entrega': 'Previsão de entrega', 
    'prioridade': 'Prioridade',
    'status': 'Status',
    'titulo': 'Título',
    'template_respostas': 'Dados do Formulário',
  };

  // Formatador de valores para exibição
  const formatValue = (field: string, value: any): string | Record<string, unknown> => {
    if (!value) return 'vazio';
    
    if (field === 'sugestao_entrega' || field === 'previsao_entrega') {
      return formatDateSafe(value);
    }
    
    // Para template_respostas, retornar o objeto original para ser formatado no componente
    if (field === 'template_respostas') {
      // Se já é um objeto, retornar como está
      if (typeof value === 'object' && value !== null) {
        return value as Record<string, unknown>;
      }
      // Se é uma string JSON, tentar fazer parse
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    }
    
    if (field === 'descricao') {
      // Remove HTML tags e limita o texto
      const cleanText = String(value).replace(/<[^>]*>/g, '');
      return cleanText.length > 50 ? cleanText.substring(0, 50) + '...' : cleanText;
    }
    
    return value.toString();
  };

  // Processar changes da edição
  const changes = [];
  if (historico.edicao && historico.edicao.changes) {
    for (const [field, oldValue] of Object.entries(historico.edicao.changes)) {
      const fieldLabel = fieldLabels[field] || field;
      // O valor novo vem do estado posterior (próxima edição ou demanda atual)
      const newValue = estadoPosterior?.[field] || 'não definido';
      
      changes.push({
        field,
        fieldLabel,
        from: formatValue(field, oldValue), // Valor anterior (do changes)
        to: formatValue(field, newValue)    // Valor posterior (reconstruído)
      });
    }
  }

  // Processar comentários se existirem
  const details: any = {};
  if (historico.comentario && historico.comentario.trim()) {
    details.comment = historico.comentario.trim();
  }

  return {
    id: historico.id.toString(),
    type: (customDescription && customDescription.includes('reenviou')) ? 'status_changed' as const : 'updated' as const,
    user: {
      id: historico.editor.id,
      name: historico.editor.name,
      email: historico.editor.email,
      funcao_usuario: historico.editor.funcao_usuario,
      cargo_usuario: historico.editor.cargo_usuario,
    },
    timestamp: historico.created_at,
    description: customDescription || 'editou a demanda',
    expandable: true,
    expanded: false,
    changes,
    details: Object.keys(details).length > 0 ? details : undefined
  };
};

// REMOVIDO: transformEdicaoToHistoryItems - não é mais usado
// Todas as edições são agora processadas via histórico com a lógica expansível

interface UseHistoricoResult {
  historico: HistoryItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para buscar e gerenciar histórico de uma demanda
 */
export const useHistorico = (demandaId?: number): UseHistoricoResult => {
  const [historico, setHistorico] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Controle de requisições para evitar duplicações
  const fetchingRef = useRef<boolean>(false);
  const lastFetchedIdRef = useRef<number | null>(null);

  const fetchHistorico = async (force: boolean = false) => {
    if (!demandaId) {
      setHistorico([]);
      return;
    }

    // Evitar requisições duplicadas
    if (!force && (fetchingRef.current || lastFetchedIdRef.current === demandaId)) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      const data = await historicoService.getHistoricoDemanda(demandaId);
      
      // Verificar se os dados existem e são válidos
      if (!data || (!data.historico && !data.edicoes)) {
        console.warn('Dados de histórico vazios ou inválidos:', data);
        setHistorico([]);
        lastFetchedIdRef.current = demandaId;
        return;
      }

      // Transformar dados do histórico
      const historicoItems: HistoryItem[] = [];
      
      if (data.historico && Array.isArray(data.historico)) {
        // Separar histórico em categorias
        const historicoComEdicao = data.historico.filter(h => h.edicao && h.edicao.changes && h.editor?.id);
        const historicoSemEdicao = data.historico.filter(h => (!h.edicao || !h.edicao.changes) && h.editor?.id);
        
        // Processar histórico sem edições normalmente
        historicoSemEdicao.forEach(histItem => {
          historicoItems.push(transformHistoricoToHistoryItem(histItem));
        });
        
        // Para histórico COM edições, precisamos decidir:
        // - Se a ação é específica (autorizou, reprovou, solucionou, etc.) → mostrar a ação específica com detalhes da mudança
        // - Se a ação é genérica (editou) → mostrar como card expansível com todas as mudanças
        if (historicoComEdicao.length > 0) {
          // Ordenar por timestamp (mais antigo primeiro para reconstruir cronologia)
          historicoComEdicao.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          
          historicoComEdicao.forEach((histItem, index) => {
            const acaoLower = histItem.acao.toLowerCase();
            
            // Tratamento especial para "reenviou" - sempre mostrar com mudanças se existirem
            if (acaoLower.includes('reenviou')) {
              // Verificar se há mudanças reais (além de campos vazios)
              const hasChanges = histItem.edicao?.changes && Object.keys(histItem.edicao.changes).length > 0;
              
              if (hasChanges) {
                // Mostrar como card expansível com descrição customizada
                const proximaEdicao = index < historicoComEdicao.length - 1 ? historicoComEdicao[index + 1] : null;
                const estadoPosterior = (proximaEdicao && proximaEdicao.edicao) ? proximaEdicao.edicao.snapshot : data.demanda_atual;
                
                const itemWithChanges = transformHistoricoToHistoryItemWithChanges(
                  histItem, 
                  estadoPosterior,
                  'reenviou a demanda' // Descrição customizada
                );
                historicoItems.push(itemWithChanges);
              } else {
                // Sem mudanças, mostrar como ação simples
                historicoItems.push(transformHistoricoToHistoryItem(histItem));
              }
              return; // Pula para o próximo item
            }
            
            // Verificar se é uma ação específica que deve ser exibida diretamente
            const isSpecificAction =
              acaoLower.includes('autorizou') ||
              acaoLower.includes('reprovou') ||
              acaoLower.includes('aprovou') ||
              acaoLower.includes('recusou') ||
              acaoLower.includes('aceitou') ||
              acaoLower.includes('solucion') ||
              acaoLower.includes('desfez') ||
              acaoLower.includes('finaliz') ||
              acaoLower.includes('devol') ||
              acaoLower.includes('puxou') ||
              acaoLower.includes('puxar') ||
              acaoLower.includes('atribuiu') ||
              acaoLower.includes('errada') ||
              acaoLower.includes('remand') ||
              acaoLower.includes('reenviou') ||
              acaoLower.includes('excluiu') ||
              acaoLower.includes('removeu um atribuido') ||
              acaoLower.includes('removeu um atribuído') ||
              (acaoLower.includes('desatrib') && acaoLower.includes('demanda'));
            
            if (isSpecificAction) {
              // Mostrar como ação específica com detalhes extraídos da edição
              historicoItems.push(transformHistoricoToHistoryItem(histItem));
            } else {
              // Mostrar como card expansível (edição genérica)
              const proximaEdicao = index < historicoComEdicao.length - 1 ? historicoComEdicao[index + 1] : null;
              const estadoPosterior = (proximaEdicao && proximaEdicao.edicao) ? proximaEdicao.edicao.snapshot : data.demanda_atual;
              
              const itemWithChanges = transformHistoricoToHistoryItemWithChanges(histItem, estadoPosterior);
              historicoItems.push(itemWithChanges);
            }
          });
        }
      }
      
      // REMOVIDO: Não processar mais edições sem histórico como itens separados
      // Agora todas as edições são processadas via histórico com a nova lógica expansível
      // A lógica transformEdicaoToHistoryItems é mantida apenas para casos excepcionais
      
      // Ordenar por timestamp (mais recente primeiro)
      historicoItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setHistorico(historicoItems);
      lastFetchedIdRef.current = demandaId;
    } catch (err: any) {
      console.error('Erro ao buscar histórico:', err);
      lastFetchedIdRef.current = null;
      
      // Verificar se é um erro de resposta da API
      if (err.response) {
        const apiError = err.response.data;
        console.error('Detalhes do erro da API:', apiError);
        
        // Se a API retornou detalhes do erro, mostrar
        if (apiError.message) {
          setError(`Erro no servidor: ${apiError.message}`);
        } else {
          setError(`Erro HTTP ${err.response.status}: ${err.response.statusText}`);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao carregar histórico');
      }
      
      setHistorico([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Resetar o controle quando o demandaId mudar
    if (lastFetchedIdRef.current !== demandaId) {
      lastFetchedIdRef.current = null;
    }
    fetchHistorico();
  }, [demandaId]);

  // Modificar refetch para forçar nova requisição
  const refetchHistorico = async () => {
    lastFetchedIdRef.current = null;
    await fetchHistorico(true);
  };

  return {
    historico,
    loading,
    error,
    refetch: refetchHistorico,
  };
};
